import StaffProfiles, { type StaffPerson } from '../components/StaffProfiles';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient, getApiBaseUrl } from '../api/client';
import EventFallbackPattern from '../components/EventFallbackPattern';
import Modal from '../components/Modal';
import { getContrastText } from '../utils/colorContrast';
import { shouldRevealCapacity } from '../utils/capacityVisibility';
import { PublicEventDetailContent } from './PublicEventPage';
import './PublicEventsListPage.css';
import { translateError } from '../utils/errorMessages';

/**
 * Get the full URL for an image.
 * If the URL is relative (starts with /), prepend the API base URL.
 */
const getImageUrl = (url: string): string => {
  if (url.startsWith('/')) {
    return `${getApiBaseUrl()}${url}`;
  }
  return url;
};

const stripMarkdown = (md: string): string =>
  md
    .replace(/```[\s\S]*?```/g, '')
    .replace(/!\[.*?\]\(.*?\)/g, '')
    .replace(/\[([^\]]*?)\]\([^)]*?\)/g, '$1')
    .replace(/^#+\s*/gm, '')
    .replace(/^\s*>\s?/gm, '')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/^\s*\d+\.\s+/gm, '')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/(\*\*|__)(?=\S)([\s\S]+?)(?<=\S)\1/g, '$2')
    .replace(/(?<![A-Za-z0-9_])([*_])(?=\S)([\s\S]+?)(?<=\S)\1(?![A-Za-z0-9_])/g, '$2')
    .replace(/~~(?=\S)([\s\S]+?)(?<=\S)~~/g, '$1')
    .replace(/\n{2,}/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

interface IPublicEventSummary {
  responsible?: StaffPerson | null;
  _id: string;
  name: string;
  description: string;
  extendedDescription?: string;
  location?: string;
  startTime: string;
  endTime: string;
  price: number | string;
  capacity?: number;
  remainingSlots?: number | null;
  waitlistCount?: number;
  publicSlug: string;
  registeredCount: number;
  gallery?: string[];
  colorSettings?: {
    primaryColor?: string;
    secondaryColor?: string;
  };
}

const PublicEventsListPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [events, setEvents] = useState<IPublicEventSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSlug, setActiveSlug] = useState<string | null>(null);
  const [activeName, setActiveName] = useState<string>('');

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const { data } = await apiClient.get<IPublicEventSummary[]>('/api/public/events');
        setEvents(data);
        setLoading(false);
      } catch (err: any) {
        setError(translateError(err, t, 'publicEvents.loadError'));
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const locale = i18n.language === 'cs' ? 'cs-CZ' : 'en-US';

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'long',
    });
  };

  const formatDateFull = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isMultiDay = (startStr: string, endStr: string) => {
    const s = new Date(startStr);
    const e = new Date(endStr);
    return s.toDateString() !== e.toDateString();
  };

  if (loading) {
    return (
      <div className="public-events-container">
        <div className="public-events-header">
          <h1>{t('publicEvents.title')}</h1>
          <p className="subtitle">{t('publicEvents.subtitle')}</p>
        </div>
        <div className="events-grid" aria-busy="true" aria-label={t('common.loading')}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="skeleton-card">
              <div className="skeleton-media skeleton-block" />
              <div className="skeleton-body">
                <div className="skeleton-line s-badge skeleton-block" />
                <div className="skeleton-line s-title skeleton-block" />
                <div className="skeleton-line s-date skeleton-block" />
                <div className="skeleton-line s-loc skeleton-block" />
                <div className="skeleton-line s-desc-1 skeleton-block" />
                <div className="skeleton-line s-desc-2 skeleton-block" />
                <div className="skeleton-line s-desc-3 skeleton-block" />
                <div className="skeleton-stats">
                  <div className="skeleton-line s-price skeleton-block" />
                  <div className="skeleton-line s-count skeleton-block" />
                </div>
                <div className="skeleton-line s-cta skeleton-block" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-events-container">
        <p className="error-message">{error}</p>
      </div>
    );
  }

  return (
    <div className="public-events-container">
      <div className="public-events-header">
        <h1>{t('publicEvents.title')}</h1>
        <p className="subtitle">{t('publicEvents.subtitle')}</p>
      </div>

      {events.length === 0 ? (
        <div className="no-events">
          <p>{t('publicEvents.noEvents')}</p>
        </div>
      ) : (
        <div className="events-grid">
          {events.map((event) => {
            const capacity = event.capacity ?? 0;
            const isFull = capacity > 0 && event.remainingSlots === 0;
            const hasWaitlist = (event.waitlistCount || 0) > 0;
            // Quiet events hide their numbers; see utils/capacityVisibility.
            const showCapacity = isFull || shouldRevealCapacity(event.registeredCount, capacity);
            const hasImage = !!(event.gallery && event.gallery.length > 0);
            const priceNum = typeof event.price === 'number' ? event.price : parseFloat(event.price ?? '0') || 0;

            const primaryColor = event.colorSettings?.primaryColor || 'var(--orange-primary)';
            const secondaryColor = event.colorSettings?.secondaryColor || primaryColor;

            const rawPreview = (event.extendedDescription?.trim() || event.description?.trim() || '');
            const descriptionPreview = stripMarkdown(rawPreview);

            const dateLabel = isMultiDay(event.startTime, event.endTime)
              ? `${formatDate(event.startTime)} – ${formatDate(event.endTime)}`
              : formatDateFull(event.startTime);
            const timeLabel = `${formatTime(event.startTime)} – ${formatTime(event.endTime)}`;

            const ctaTextColor = getContrastText(secondaryColor);

            const cardStyle = {
              '--event-primary': primaryColor,
              '--event-secondary': secondaryColor,
              '--event-cta-text': ctaTextColor,
            } as React.CSSProperties;

            return (
              <article
                key={event._id}
                className="event-card"
                style={cardStyle}
              >
                <div className={`event-card-media${hasImage ? '' : ' event-card-media--fallback'}`}>
                  {hasImage ? (
                    <img
                      src={getImageUrl(event.gallery![0])}
                      alt=""
                      onError={(e) => {
                        const img = e.target as HTMLImageElement;
                        img.style.display = 'none';
                        img.parentElement?.classList.add('event-card-media--fallback');
                      }}
                    />
                  ) : (
                    <EventFallbackPattern seed={String(event._id)} />
                  )}
                  <div className="event-card-media-overlay">
                    <h2 className="event-card-title">{event.name}</h2>
                  </div>
                </div>

                <div className="event-card-body">
                  {capacity > 0 && showCapacity && (
                    <span className={`event-card-badge ${isFull ? 'event-card-badge--full' : 'event-card-badge--available'}`}>
                      {isFull
                        ? t('publicEvents.full')
                        : t('publicEvents.spotsAvailable', { count: event.remainingSlots ?? 0 })}
                    </span>
                  )}

                  <div className="event-card-when">
                    <span className="event-card-meta-icon" aria-hidden="true">📅</span>
                    <span className="event-card-date">{dateLabel}</span>
                    <span className="event-card-when-sep">·</span>
                    <span className="event-card-time">{timeLabel}</span>
                  </div>

                  {event.location && (
                    <div className="event-card-location">
                      <span className="event-card-meta-icon" aria-hidden="true">📍</span>
                      <span>{event.location}</span>
                    </div>
                  )}

                  <StaffProfiles people={event.responsible ? [event.responsible] : []} />
                  {descriptionPreview && (
                    <p className="event-card-description">{descriptionPreview}</p>
                  )}

                  {(priceNum > 0 || (capacity > 0 && showCapacity)) && (
                    <div className="event-card-stats">
                      {priceNum > 0 && (
                        <span className="event-card-price">
                          {priceNum.toLocaleString(locale)}
                          <span className="event-card-price-unit">CZK</span>
                        </span>
                      )}
                      {capacity > 0 && showCapacity && (
                        <span className="event-card-count">
                          {t('publicEvents.registered', {
                            current: event.registeredCount,
                            total: capacity,
                          })}
                          {hasWaitlist && (
                            <span className="waitlist-info">
                              {' '}+ {event.waitlistCount} {t('publicEvents.inWaitlist')}
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                  )}

                  <button
                    type="button"
                    className="event-card-cta"
                    onClick={() => {
                      setActiveSlug(event.publicSlug);
                      setActiveName(event.name);
                    }}
                  >
                    <span>{isFull ? t('publicEvents.joinWaitlist') : t('publicEvents.registerNow')}</span>
                    <span className="event-card-cta-arrow" aria-hidden="true">→</span>
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <Modal
        isOpen={!!activeSlug}
        onClose={() => setActiveSlug(null)}
        title={activeName}
        size="xlarge"
      >
        {activeSlug && <PublicEventDetailContent slug={activeSlug} />}
      </Modal>
    </div>
  );
};

export default PublicEventsListPage;
